import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { usersApi } from "@/lib/api-users";

/**
 * Admin oversight of a user's API keys — LIST + REVOKE only (ADR-0052).
 *
 * An operator can no longer mint a key "on behalf of" another user (that was a
 * persistent, un-audited impersonation credential): a personal token is
 * self-serve from the user's own Profilo. This panel is the visibility +
 * kill-switch surface; a machine integration is a Service Account instead.
 */
export function ApiKeysPanel({ userId }: { userId: string }) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["users", userId, "api-keys"],
    queryFn: () => usersApi.listApiKeys(userId),
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => usersApi.revokeApiKey(userId, keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users", userId, "api-keys"] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          API keys ({query.data?.filter((k) => !k.revoked_at).length ?? 0} active)
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Personal tokens are self-serve from the user&apos;s own Profilo — an
        operator can view and revoke them here, but not create one on their
        behalf. For a machine integration, provision a Service Account.
      </p>

      <div className="rounded-lg overflow-hidden border border-border/60">
        <Table>
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Created</Th>
              <Th>Last used</Th>
              <Th>Expires</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {query.data?.length === 0 && (
              <Tr>
                <Td colSpan={6} className="text-center text-xs text-muted-foreground py-6">
                  No API keys generated.
                </Td>
              </Tr>
            )}
            {query.data?.map((k) => (
              <Tr key={k.key_id}>
                <Td className="font-medium">
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                    {k.name ?? <span className="text-muted-foreground italic">unnamed</span>}
                  </div>
                </Td>
                <Td className="text-muted-foreground text-xs">
                  {new Date(k.created_at).toLocaleString()}
                </Td>
                <Td className="text-muted-foreground text-xs">
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}
                </Td>
                <Td className="text-muted-foreground text-xs">
                  {k.expires_at ? new Date(k.expires_at).toLocaleString() : "—"}
                </Td>
                <Td>
                  {k.revoked_at ? (
                    <Badge tone="deprecated">revoked</Badge>
                  ) : (
                    <Badge tone="active">active</Badge>
                  )}
                </Td>
                <Td className="text-right">
                  {!k.revoked_at && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Revoke API key "${k.name ?? k.key_id}"?`)) {
                          revokeMutation.mutate(k.key_id);
                        }
                      }}
                    >
                      <XCircle className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                      Revoke
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    </div>
  );
}
